import { defineStore } from "pinia";
import { ref } from "vue";
import Api from "@/services/Api";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";
import type { ClientModel } from "@/models/ClientModel";

export const useClientStore = defineStore("client", () => 
{
    const CACHE_KEY = "store_client_data";
    const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds
    const list = ref<Array<ClientModel>>([]);
    const isInitialized = ref<boolean>(false);
    const lastLoadTime = ref<number>(0);
    let initPromise: Promise<void> | null = null;

    const init = async (): Promise<void> => {
        // Check if cache is still valid
        const now = Date.now();
        if (isInitialized.value && (now - lastLoadTime.value) < CACHE_TTL) {
            return; // Cache is still valid
        }
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first (only if not expired)
                if (!isInitialized.value) {
                    const cached: Array<ClientModel> | undefined = await Cache.get(CACHE_KEY);
                    if (cached && Array.isArray(cached) && cached.length > 0) {
                        list.value = cached;
                        isInitialized.value = true;
                        lastLoadTime.value = now;
                        return;
                    }
                }
                
                // Load from server if cache is empty or expired
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };

    const reload = async (): Promise<void> =>
    {
        list.value = await Api.loadClients();
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        lastLoadTime.value = Date.now();
    };

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const getByCustomerNumber = async(cust: number): Promise<ClientModel | null> => 
    {
        await init();

        for (let i = 0; i < list.value.length; i++)
        {
            const row = list.value[i];
            if (row.CustomerNumber === cust)
            {
                return row;
            } 
        }

        return null;
    };

    const getById = async (id: number): Promise<ClientModel | null> =>
    {
        await init();

        for (let i = 0; i < list.value.length; i++)
        {
            const row = list.value[i];
            if (row.Id === id)
            {
                return row;
            } 
        }

        return null;
    };

    // Register refresh event listener
    Emitter.on("refresh:clientStore", async () => {
        console.debug("refresh:clientStore event received");
        await refresh();
    });

    // Load method for explicit initialization
    const load = async (): Promise<void> => {
        await init();
    };

    return { list, load, reload, refresh, getById, getByCustomerNumber }

});
